import { ConfigProvider, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalBlockLabelStyle,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { PopupScheduleBlock } from "components/WeeklyCalendar/components/Event";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { InstructorColorProvider } from "context/InstructorColorProvider";
import { checkScheduleClashesV2 } from "hooks/api/schedule-v2";
import { useCallback, useState } from "react";
import { translate } from "shared/languages";
import {
  ScheduleClashCheck,
  ScheduleClashCheckForm,
  ScheduleClashDetail,
} from "shared/types";

const { Text } = Typography;

// One clashing session and why it clashes (with the same instructor and/or a
// client), so a single session is listed once with a generic reason.
interface MergedClash {
  session: ScheduleClashDetail;
  instructorClash: boolean;
  clientClash: boolean;
}

// Dedup on the actual occurrence: schedule_id + start_time. A private class that
// clashes for both its instructor and a client shares one key; genuinely
// different clashing sessions get different keys.
const clashKey = (clash: ScheduleClashDetail) =>
  `${clash.schedule_id}-${clash.start_time}`;

const mergeClashes = (clashes: ScheduleClashCheck): MergedClash[] => {
  const merged = new Map<string, MergedClash>();
  const ensure = (clash: ScheduleClashDetail) => {
    const key = clashKey(clash);
    let entry = merged.get(key);
    if (!entry) {
      entry = { session: clash, instructorClash: false, clientClash: false };
      merged.set(key, entry);
    }
    return entry;
  };

  clashes.instructor_clashes.forEach((clash) => {
    ensure(clash).instructorClash = true;
  });
  clashes.client_clashes.forEach((clash) => {
    ensure(clash).clientClash = true;
  });

  return Array.from(merged.values());
};

const DEFAULT_CLIENT_REASON_KEY = "clash.reason_client";

// Generic reason line for a deduped session (no names). The client-only reason
// is configurable so the booking flow can phrase it from the customer's side.
const reasonKey = (merged: MergedClash, clientReasonKey: string): string => {
  if (merged.instructorClash && merged.clientClash) return "clash.reason_both";
  if (merged.instructorClash) return "clash.reason_instructor";
  return clientReasonKey;
};

// Adapt a clash detail to the shape the shared popup block renders, so the
// modal reuses the exact event-block styling (instructor colour, class name,
// time range) instead of a bespoke card.
const toCalendarSchedule = (
  clash: ScheduleClashDetail,
  index: number,
): CalendarSchedule => ({
  schedule_id: `clash-${index}`,
  general_schedule_id: `clash-${index}`,
  occurrence_timestamp: new Date(clash.start_time).toISOString(),
  start_time: new Date(clash.start_time),
  end_time: new Date(clash.end_time),
  instructor_id: clash.instructor_id,
  instructor_name: clash.instructor_name,
  staff_id: clash.instructor_id,
  staff_name: clash.instructor_name,
  customer_name: "",
  customer_names: [],
  is_cancelled: false,
  is_recurring: false,
  class_name: clash.class_name,
  need_action: false,
});

const ClashRow = ({
  merged,
  index,
  clientReasonKey,
}: {
  merged: MergedClash;
  index: number;
  clientReasonKey: string;
}) => (
  <div>
    {/* Generic reason for this one session: same instructor and/or client. */}
    <Text style={modalBlockLabelStyle}>
      {translate(reasonKey(merged, clientReasonKey))}
    </Text>
    {/* The shared full-size popup block — presentational and non-interactive,
        safe to render outside the calendar's DragProvider. */}
    <PopupScheduleBlock event={toCalendarSchedule(merged.session, index)} />
  </div>
);

const ClashWarningContent = ({
  clashes,
  clientReasonKey,
}: {
  clashes: ScheduleClashCheck;
  clientReasonKey: string;
}) => (
  <div>
    <Text style={modalSubtitleStyle}>{translate("clash.description")}</Text>
    {mergeClashes(clashes).map((merged, index) => (
      <ClashRow
        key={clashKey(merged.session)}
        merged={merged}
        index={index}
        clientReasonKey={clientReasonKey}
      />
    ))}
  </div>
);

const hasClashes = (clashes: ScheduleClashCheck) =>
  clashes.instructor_clashes.length > 0 || clashes.client_clashes.length > 0;

const ClashWarningModal = ({
  open,
  clashes,
  clientReasonKey,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  clashes: ScheduleClashCheck | null;
  clientReasonKey: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  // Popup button-color system: navy "Konfirmasi" (proceed-anyway confirm, not
  // destructive) and solid-ink "Batal" dismiss.
  <ConfigProvider theme={modalButtonsTheme}>
    <Modal
      open={open}
      title={<ModalHeader tone="error" title={translate("clash.title")} />}
      okText={translate("button.confirm")}
      cancelText={translate("button.cancel")}
      onOk={onConfirm}
      onCancel={onCancel}
      width={400}
      destroyOnClose
    >
      {/* The popup block resolves instructor colours via
          InstructorColorProvider — the same provider (and query) the calendar
          uses, so colours stay consistent. */}
      {clashes && (
        <InstructorColorProvider>
          <ClashWarningContent
            clashes={clashes}
            clientReasonKey={clientReasonKey}
          />
        </InstructorColorProvider>
      )}
    </Modal>
  </ConfigProvider>
);

/**
 * Gates an action behind the first-occurrence clash check. Returns:
 *  - checkAndConfirm(form, onConfirm): runs the check; with clashes it opens the
 *    shared warning modal and only runs onConfirm on confirm, with none it runs
 *    onConfirm immediately. A failed check proceeds (clashing is allowed).
 *  - clashWarning: the modal element to render in the caller's JSX.
 *
 * Reused by the create, reschedule, and open-class booking flows so there's one
 * clash-check call and one warning modal. `clientReasonKey` overrides the
 * client-only reason line (the booking flow phrases it from the client's side).
 */
export const useClashWarning = (options?: { clientReasonKey?: string }) => {
  const clientReasonKey = options?.clientReasonKey ?? DEFAULT_CLIENT_REASON_KEY;
  const [pending, setPending] = useState<{
    clashes: ScheduleClashCheck;
    onConfirm: () => void;
  } | null>(null);

  const checkAndConfirm = useCallback(
    async (form: ScheduleClashCheckForm, onConfirm: () => void) => {
      let clashes: ScheduleClashCheck;
      try {
        clashes = await checkScheduleClashesV2(form);
      } catch (error) {
        console.error("Clash check failed; proceeding without warning.", error);
        onConfirm();
        return;
      }
      if (!hasClashes(clashes)) {
        onConfirm();
        return;
      }
      setPending({ clashes, onConfirm });
    },
    [],
  );

  // Multi-series variant (per-day-times weekly create): checks each series'
  // first occurrence and shows ONE warning modal with the concatenated results
  // (mergeClashes dedups shared sessions at render). Same failure semantics as
  // checkAndConfirm: a failed check proceeds, clashing is allowed.
  const checkManyAndConfirm = useCallback(
    async (forms: ScheduleClashCheckForm[], onConfirm: () => void) => {
      let clashes: ScheduleClashCheck;
      try {
        const results = await Promise.all(
          forms.map((form) => checkScheduleClashesV2(form)),
        );
        clashes = {
          instructor_clashes: results.flatMap((r) => r.instructor_clashes),
          client_clashes: results.flatMap((r) => r.client_clashes),
        };
      } catch (error) {
        console.error("Clash check failed; proceeding without warning.", error);
        onConfirm();
        return;
      }
      if (!hasClashes(clashes)) {
        onConfirm();
        return;
      }
      setPending({ clashes, onConfirm });
    },
    [],
  );

  const clashWarning = (
    <ClashWarningModal
      open={!!pending}
      clashes={pending?.clashes ?? null}
      clientReasonKey={clientReasonKey}
      onConfirm={() => {
        pending?.onConfirm();
        setPending(null);
      }}
      onCancel={() => setPending(null)}
    />
  );

  return { checkAndConfirm, checkManyAndConfirm, clashWarning };
};
